import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CommentIcon from "@mui/icons-material/Chat";
import { GridColumnMenu } from "@mui/x-data-grid";

import Modal from "Components/Common/Modal";
import CommentInputForReason from "Components/Common/CommentInputForReason";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import CommentBox from "Components/Common/CommentBox";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import { powerAdmin4Roles } from "common/constant";
import {
  billingOptionsForDispatchBills,
  getOptionLabel,
  getOptionValue,
} from "common/enums";

import { getStatusForApproval, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useLazyGetSecurityDepositsQuery,
  usePostSecurityDepositMutation,
  usePutUpdateSecurityDepositStatusMutation,
} from "apis/SecurityDeposit";
import { useLazyAdminCommentQuery } from "apis/ApplicationRequestComment";

import { StyledGrid } from "./style";

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const SecurityDepositReviewModal = ({
  handleClose,
  isDispatchModal = false,
  t,
}) => {
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [isRevokedCommentModalVisible, setIsRevokedCommentModalVisible] =
    useState(false);
  const [isApproveBtnClicked, setIsApproveBtnClicked] = useState(false);
  const [securityDepositIdForComments, setSecurityDepositIdForComments] =
    useState("");
  const [isSubmitBtnClicked, setIsSubmitBtnClicked] = useState(false);
  const [{ status }, setQuery] = useState({
    status: 2,
  });
  const [{ isSMS, isEmail }, setCommunicationMode] = useState({
    isSMS: false,
    isEmail: false,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [searchParams] = useSearchParams();
  const billingCycleId = searchParams.get("billingCycleId");

  const [
    getSecurityDepositData,
    { data: { data: securityDepositData } = {}, isLoading },
  ] = useLazyGetSecurityDepositsQuery();

  const [dispatchSecurityDeposit, { isLoading: isSecurityDepositDispatching }] =
    usePostSecurityDepositMutation();

  const [getAdminComments, { data: { data: commentsData = [] } = {} }] =
    useLazyAdminCommentQuery();

  const [
    updateSecurityDepositStatus,
    { isLoading: isSecurityDepositUpdating },
  ] = usePutUpdateSecurityDepositStatusMutation();

  const { roles } = useSelector((state) => state.Login);

  const securityDeposits = useMemo(
    () =>
      isDispatchModal
        ? securityDepositData ?? []
        : securityDepositData?.filter(
            ({ approvedBy, revokedBy }) =>
              getStatusForApproval({ approvedBy, revokedBy }, roles).canApprove
          ) ?? [],
    [securityDepositData, isDispatchModal]
  );

  const canSecurityDepositCalculationRevoked = useMemo(
    () =>
      !securityDeposits.some(
        ({ approvedBy, revokedBy }) =>
          getStatusForApproval({ approvedBy, revokedBy }, roles).canRevoke
      ),
    [securityDeposits]
  );

  const isAllSecurityDepositApproved = useMemo(
    () =>
      !securityDeposits.some(
        ({ approvedBy, revokedBy }) =>
          getStatusForApproval({ approvedBy, revokedBy }, roles).canApprove
      ),
    [securityDeposits]
  );

  const columns = useMemo(
    () => [
      {
        field: "consumerNumber",
        headerName: t("Consumer Number"),
        width: 190,
      },
      {
        field: "billNumber",
        headerName: t("Bill Number"),
        width: 190,
      },
      {
        field: "Payment Date",
        headerName: t("Payment Date"),
        renderCell: ({ row: { paymentDueDate } }) =>
          paymentDueDate
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "--",
        width: 190,
      },
      {
        field: "securityDeposit",
        headerName: t("Security Deposit"),
        renderCell: ({ row: { netPayable } }) => (
          <span className="text-nowrap"> &#8377; {netPayable}</span>
        ),
        width: 190,
      },
      {
        field: "status",
        headerName: t("Status"),
        renderCell: ({ row: { status } }) =>
          getOptionLabel("billCalculationStatus", status),
        width: 190,
      },
      {
        field: "comments",
        headerName: t("Comment"),
        renderCell: ({ row: { id } }) => (
          <IconButton onClick={() => setSecurityDepositIdForComments(id)}>
            <CommentIcon color="primary" />
          </IconButton>
        ),
        width: 190,
      },
    ],
    [securityDeposits, securityDepositData]
  );

  const approveBtnHandler = async () => {
    try {
      await updateSecurityDepositStatus(
        {
          securityDepositBillCalculationIds: selectedRowIds,
          isApproved: true,
        },
        "Security deposits approved successfully"
      );
    } catch (error) {
      console.error(error);
    }
  };

  const revokeBtnHandler = async (comment) => {
    try {
      await updateSecurityDepositStatus(
        {
          securityDepositBillCalculationIds: selectedRowIds,
          isApproved: false,
          comment,
        },
        "Security deposits revoked successfully"
      );
    } catch (error) {
      console.error(error);
    }
  };

  const submitBtnHandler = async () => {
    if (!isEmail && !isSMS) {
      toast.error("Select notification mode");
    } else {
      try {
        await dispatchSecurityDeposit(billingCycleId, {
          notifyModes:
            isEmail && isSMS ? [1, 2] : isEmail ? [1] : isSMS ? [2] : "",

          billCalculationIds: selectedRowIds,
        });
        handleClose();
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    if (billingCycleId) {
      (async () => {
        await getSecurityDepositData(billingCycleId, {
          calculationStatus: isDispatchModal ? status : "",
        });
      })();
    }
  }, [billingCycleId, status]);

  useEffect(() => {
    try {
      if (securityDepositIdForComments) {
        (async () =>
          await getAdminComments({
            commentType: getOptionValue("adminComments", "SecurityDeposit"),
            commentTypeId: securityDepositIdForComments,
          }))();
      }
    } catch (error) {
      console.error(error);
    }
  }, [securityDepositIdForComments]);

  return (
    <>
      <Backdrop open={isLoading} />
      {!!securityDepositIdForComments && (
        <Modal
          isOpen
          title="Comments"
          maxWidth="md"
          handleClose={() => setSecurityDepositIdForComments("")}
        >
          <div className="mt-1">
            {commentsData?.length ? (
              commentsData.map(({ comment, createdAt, name }, index) => (
                <CommentBox
                  comment={comment}
                  name={name}
                  createdAt={createdAt}
                  key={index}
                />
              ))
            ) : (
              <Card variant="outlined" className="mt-2 p-0">
                <CardContent className="pt-1">
                  <Typography color="GrayText" gutterBottom>
                    {t("No comment")}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </div>
        </Modal>
      )}

      <CommentInputForReason
        handleClose={() => {
          setIsRevokedCommentModalVisible(false);
        }}
        handleSubmit={(comment) => {
          revokeBtnHandler(comment);
          setIsRevokedCommentModalVisible(false);
        }}
        isOpen={isRevokedCommentModalVisible}
        isLoading={isSecurityDepositUpdating}
        title="Add Reason for Revoked"
        errorMessage="Please enter reason for revoked"
      />

      <ConfirmationDialog
        message="Are you sure you want to Approve selected Security Deposit?"
        onCancel={() => setIsApproveBtnClicked(false)}
        onConfirm={async () => {
          await approveBtnHandler();
          setIsApproveBtnClicked(false);
        }}
        isOpen={isApproveBtnClicked}
        isLoading={isSecurityDepositUpdating}
        title="Warning"
        variant="warning"
      />

      <ConfirmationDialog
        message="Are you sure you want to dispatch selected Security Deposit?"
        onCancel={() => setIsSubmitBtnClicked(false)}
        onConfirm={async () => {
          await submitBtnHandler();
          setIsSubmitBtnClicked(false);
        }}
        isOpen={isSubmitBtnClicked}
        isLoading={isSecurityDepositDispatching}
        title="Warning"
        variant="warning"
      />

      <Modal
        isOpen
        handleClose={handleClose}
        title={isDispatchModal ? "Dispatch Bills" : "Review Bills"}
        maxWidth="lg"
      >
        <>
          {isDispatchModal && (
            <Grid container columnSpacing={2} className="mt-2 mb-2">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <SelectField
                  className="w-100 mt-1"
                  size="small"
                  label="Status"
                  options={billingOptionsForDispatchBills}
                  value={billingOptionsForDispatchBills.find(
                    ({ value }) => value === status
                  )}
                  onChange={(_, { value }) =>
                    setQuery((prev) => ({ ...prev, status: value }))
                  }
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2} lg={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isEmail}
                      onChange={() =>
                        setCommunicationMode((prev) => ({
                          ...prev,
                          isEmail: !prev.isEmail,
                        }))
                      }
                    />
                  }
                  label={t("Email")}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2} lg={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isSMS}
                      onChange={() =>
                        setCommunicationMode((prev) => ({
                          ...prev,
                          isSMS: !prev.isSMS,
                        }))
                      }
                    />
                  }
                  label={t("SMS")}
                />
              </Grid>
            </Grid>
          )}
          <StyledGrid
            checkboxSelection
            autoHeight
            disableRowSelectionOnClick
            rows={securityDeposits ?? []}
            columns={columns}
            pageSizeOptions={[5, 10]}
            rowCount={securityDeposits?.length}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onRowSelectionModelChange={(ids) => {
              // eslint-disable-next-line no-undef
              const selectedIDs = new Set(ids);
              setIsRowSelected(!!selectedIDs.size);
              const selectedRows =
                securityDeposits
                  ?.filter((row) => selectedIDs.has(row.id))
                  .map(({ id }) => id) ?? [];
              setSelectedRowIds(selectedRows);
            }}
            slots={{
              columnMenu: CustomColumnMenu,
              noRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  {t("No records found")}.
                </Stack>
              ),
            }}
            rowSelection
            keepNonExistentRowsSelected
          />
          <div className="text-end">
            {isDispatchModal ? (
              <Button
                variant="contained"
                className="mt-2"
                disabled={isLoading || !isRowSelected}
                onClick={() => setIsSubmitBtnClicked(true)}
              >
                {t("Submit")} {isLoading && <CircularProgress size={20} />}
              </Button>
            ) : (
              <>
                {!isAuthorizedUser(roles, powerAdmin4Roles) && (
                  <Button
                    variant="contained"
                    className="mt-2 me-2"
                    color="primary"
                    disabled={
                      canSecurityDepositCalculationRevoked || !isRowSelected
                    }
                    onClick={() => setIsRevokedCommentModalVisible(true)}
                  >
                    {t("Revoke")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  className="mt-2 "
                  color="success"
                  disabled={isAllSecurityDepositApproved || !isRowSelected}
                  onClick={() => setIsApproveBtnClicked(true)}
                >
                  {t("Approve")}
                </Button>
              </>
            )}
          </div>
        </>
      </Modal>
    </>
  );
};

SecurityDepositReviewModal.propTypes = {
  handleClose: PropTypes.func,
  isDispatchModal: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(SecurityDepositReviewModal);
